import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  XCircle,
  Lightbulb,
  ArrowRight,
  RotateCcw,
} from "lucide-react";
import MasterKaizenHint from "./MasterKaizenHint";

interface Step {
  title: string;
  explanation: string;
  practice?: {
    problem: string;
    question: string;
    answer: string;
    hint?: string;
    intermediateSteps?: {
      label: string;
      answer: string;
      hint?: string;
    }[];
  };
}

interface StepByStepPracticeProps {
  techniqueName: string;
  steps: Step[];
  finalExample?: {
    problem: string;
    fullSolution: string;
  };
}

export default function StepByStepPractice({
  techniqueName,
  steps,
  finalExample,
}: StepByStepPracticeProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [intermediateAnswers, setIntermediateAnswers] = useState<
    Record<number, string>
  >({});
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState<"correct" | "incorrect" | null>(
    null,
  );
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const hasIntermediateSteps =
    currentStep.practice?.intermediateSteps &&
    currentStep.practice.intermediateSteps.length > 0;

  const checkAnswer = () => {
    if (!currentStep.practice) return;

    if (hasIntermediateSteps) {
      // Check all intermediate steps
      const allCorrect = currentStep.practice.intermediateSteps!.every(
        (step, idx) => {
          const userAns = intermediateAnswers[idx]?.trim().toLowerCase();
          const correctAns = step.answer.trim().toLowerCase();
          return userAns === correctAns;
        },
      );

      if (allCorrect) {
        setFeedback("correct");
        setCompletedSteps(new Set([...completedSteps, currentStepIndex]));
      } else {
        setFeedback("incorrect");
      }
    } else {
      // Check single answer
      const correct =
        userAnswer.trim().toLowerCase() ===
        currentStep.practice.answer.trim().toLowerCase();
      setFeedback(correct ? "correct" : "incorrect");
      if (correct) {
        setCompletedSteps(new Set([...completedSteps, currentStepIndex]));
      }
    }
  };

  const nextStep = () => {
    if (!isLastStep) {
      setCurrentStepIndex(currentStepIndex + 1);
      setUserAnswer("");
      setIntermediateAnswers({});
      setFeedback(null);
      setShowHint(false);
    }
  };

  const resetProgress = () => {
    setCurrentStepIndex(0);
    setUserAnswer("");
    setIntermediateAnswers({});
    setFeedback(null);
    setShowHint(false);
    setCompletedSteps(new Set());
  };

  const setIntermediateAnswer = (index: number, value: string) => {
    setIntermediateAnswers({ ...intermediateAnswers, [index]: value });
  };

  const allStepsCompleted = completedSteps.size === steps.length;

  return (
    <Card className="border-2 border-indigo-200">
      <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl">
            Interactive Tutorial: {techniqueName}
          </CardTitle>
          <Badge variant="outline" className="bg-white">
            Step {currentStepIndex + 1} of {steps.length}
          </Badge>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex gap-2">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  completedSteps.has(idx)
                    ? "bg-green-500"
                    : idx === currentStepIndex
                      ? "bg-indigo-500"
                      : "bg-gray-200"
                }`}
              />
            ))}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-6">
        {!allStepsCompleted ? (
          <div className="space-y-6">
            {/* Step Explanation */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-2 text-blue-900 dark:text-blue-100">
                {currentStep.title}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {currentStep.explanation}
              </p>
            </div>

            {/* Practice Problem */}
            {currentStep.practice && (
              <div className="space-y-4">
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2 text-amber-900 dark:text-amber-100">
                    Practice This Step:
                  </h4>
                  <p className="text-lg font-mono mb-2">
                    {currentStep.practice.problem}
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    {currentStep.practice.question}
                  </p>
                </div>

                {/* Answer Input */}
                <div className="space-y-3">
                  {hasIntermediateSteps ? (
                    // Multiple intermediate inputs
                    currentStep.practice.intermediateSteps!.map((step, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <label className="text-sm font-medium min-w-[120px]">
                          {step.label}:
                        </label>
                        <Input
                          type="text"
                          value={intermediateAnswers[idx] || ""}
                          onChange={(e) =>
                            setIntermediateAnswer(idx, e.target.value)
                          }
                          placeholder="Your answer"
                          disabled={feedback === "correct"}
                          className="font-mono text-lg"
                        />
                        {showHint && step.hint && (
                          <span className="text-sm text-blue-600 dark:text-blue-400 italic">
                            Hint: {step.hint}
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    // Single answer input
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium">
                        Your answer:
                      </label>
                      <Input
                        type="text"
                        value={userAnswer}
                        onChange={(e) => setUserAnswer(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && !feedback && checkAnswer()
                        }
                        placeholder="Type your answer"
                        disabled={feedback === "correct"}
                        className="font-mono text-lg flex-1"
                      />
                    </div>
                  )}

                  {/* Feedback */}
                  {feedback && (
                    <div
                      className={`flex items-center gap-2 p-3 rounded-lg ${
                        feedback === "correct"
                          ? "bg-green-50 text-green-800 border border-green-200"
                          : "bg-red-50 text-red-800 border border-red-200"
                      }`}
                    >
                      {feedback === "correct" ? (
                        <>
                          <CheckCircle2 className="w-5 h-5" />
                          <span className="font-medium">
                            Correct! Great job!
                          </span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5" />
                          <span className="font-medium">
                            Not quite. Try again or use a hint.
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  {feedback !== "correct" && (
                    <>
                      <Button
                        onClick={checkAnswer}
                        variant="default"
                        className="flex-1"
                      >
                        Check Answer
                      </Button>
                      <Button
                        onClick={() => setShowHint(!showHint)}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Lightbulb className="w-4 h-4" />
                        {showHint ? "Hide Wisdom" : "Ask Sensei"}
                      </Button>
                    </>
                  )}

                  {feedback === "correct" && !isLastStep && (
                    <Button
                      onClick={nextStep}
                      className="flex-1 flex items-center justify-center gap-2"
                    >
                      Next Step
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  )}

                  {feedback === "correct" && isLastStep && (
                    <Button
                      onClick={nextStep}
                      className="flex-1"
                      variant="default"
                    >
                      Complete Tutorial
                    </Button>
                  )}
                </div>

                {/* Hint Display */}
                {currentStep.practice.hint && (
                  <MasterKaizenHint
                    hint={currentStep.practice.hint}
                    show={showHint}
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          // Completion Screen
          <div className="text-center space-y-6 py-8">
            <div className="flex justify-center">
              <div className="rounded-full bg-green-100 p-6">
                <CheckCircle2 className="w-16 h-16 text-green-600" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Excellent Work!
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                You've completed all steps for {techniqueName}
              </p>
            </div>

            {finalExample && (
              <div className="bg-gray-50 dark:bg-gray-800 border rounded-lg p-6 text-left">
                <h4 className="font-semibold mb-3">Final Example:</h4>
                <div className="space-y-2">
                  <div>
                    <span className="font-medium">Problem: </span>
                    <span className="font-mono text-lg">
                      {finalExample.problem}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Complete Solution: </span>
                    <span className="font-mono text-lg text-green-600">
                      {finalExample.fullSolution}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <Button
                onClick={resetProgress}
                variant="outline"
                className="flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Practice Again
              </Button>
              <Button variant="default" asChild>
                <a href={`/practice`}>Go to Practice Arena</a>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
